/**
 * Marketing PDF Generator
 * Handles PDF generation specifically for Marketing strategy
 */

import {
  handleImageUrl,
  getChartData,
  cashFlowAccumulationCalc,
  expensesSection,
  revenuesSection,
  appreciationSection,
  graphSection,
} from '../utils/pdfUtils'
import { currencyFormat, percentageFormat } from '@/utils/number'

import {
  sum,
  map,
  zip,
  compact,
} from '@/utils/lodash'
import BasePDFGenerator from './BasePDFGenerator'
import { buildProformaDescriptionPdfNode, buildProformaDisclaimerPdfNode } from '@/utils/propertyDescription'

export class MarketingPDFGenerator extends BasePDFGenerator {
  /**
   * Hero price for Marketing: resellPriceTurnkey, else ARV turnkey.
   */
  getPage1HeroPrice() {
    const { acquisition = {} } = this.property
    const afterRepairValue = acquisition.analytics.turnkey.arvTotalTurnkey
      ? acquisition.analytics.turnkey.arvTotalTurnkey
      : 0
    const offerPrice = acquisition.analysis.resellPriceTurnkey
      ? acquisition.analysis.resellPriceTurnkey
      : afterRepairValue
    return offerPrice
  }

  /**
   * Generate the complete Marketing PDF document
   */
  async generatePDF() {
    return this.generateTwoPageDocDefinition()
  }

  /**
   * Generate page 1 lower right section
   */
  async generatePage1LowerRight() {
    const { property, urlAffiliate, urlDomain } = this
    const {
      beds = [0],
      baths = [0],
      sqft = [0],
      acquisition = {},
      assumptions = {},
      images = [],
      proformaImages = [],
      portfolio = {},
      proformaContact,
    } = property
    const { organization = {} } = portfolio

    const whiteImage = await handleImageUrl('/images/white.jpg')

    const IRR = acquisition.analytics.turnkey.IRRTurnkey
      ? (acquisition.analytics.turnkey.IRRTurnkey * 100).toFixed(2)
      : 0
    const CAPRate = acquisition.analytics.turnkey.capRateTurnkey
      ? (acquisition.analytics.turnkey.capRateTurnkey * 100).toFixed(
          2,
        )
      : 0
    const cashOnCash = acquisition.analytics.turnkey
      .cashOnCashReturnTurnkey
      ? (
          acquisition.analytics.turnkey.cashOnCashReturnTurnkey * 100
        ).toFixed(2)
      : 0
    const equity = acquisition.analytics.turnkey.equityTurnkey
      ? acquisition.analytics.turnkey.equityTurnkey
      : 0
    const mortgagePayment = acquisition.analytics.turnkey
      .mortgageTurnkey
      ? acquisition.analytics.turnkey.mortgageTurnkey
      : 0
    const utilities = acquisition.analytics.turnkey
      .utilitiesTypeDollarTurnkey
      ? acquisition.analytics.turnkey.utilitiesTypeDollarTurnkey
      : 0
    const assetManagementFeeTotal = acquisition.analytics.turnkey
      .assetManagementFeeTotalTurnkey
      ? acquisition.analytics.turnkey.assetManagementFeeTotalTurnkey
      : 0
    const homeInsurance = acquisition.analytics.turnkey
      .propertyInsuranceDollarTurnkey
      ? acquisition.analytics.turnkey.propertyInsuranceDollarTurnkey
      : 0

    const rentAmount =
      assumptions.rentType === 'rentRange'
        ? acquisition.analysis.rentLow &&
          acquisition.analysis.rentHigh
          ? map(
              zip(
                acquisition.analysis.rentLow,
                acquisition.analysis.rentHigh,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rent
          ? acquisition.analysis.rent
          : [0]
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.turnkey.hoaTotal
      ? acquisition.analytics.turnkey.hoaTotal / 12
      : 0

    const vacancy = assumptions.vacancy ? assumptions.vacancy : 0
    const maintenance = assumptions.maintenanceTurnkey
      ? assumptions.maintenanceTurnkey
      : 0
    const propertyMgmtPercent = acquisition.analytics.turnkey
      .propertyManagementPercentageTurnkey
      ? acquisition.analytics.turnkey
          .propertyManagementPercentageTurnkey
      : 0
    const otherExpenses = assumptions.otherExpenseTurnkey
      ? assumptions.otherExpenseTurnkey
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const yearlyHOA = monthlyHOA * 12
    const vacancyRateMonthly = sum(
      map(
        zip(rentAmount, vacancy),
        ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
      ),
    )
    const grossRentalIncomeMonthly =
      sum(rentAmount) - vacancyRateMonthly

    const totalExpenseMonthly =
      propertyTaxes / 12 +
      homeInsurance / 12 +
      monthlyHOA +
      (propertyMgmtPercent / 100) * grossRentalIncomeMonthly +
      (maintenance / 100) * grossRentalIncomeMonthly +
      utilities +
      assetManagementFeeTotal / 12 +
      otherExpenses / 12 +
      customAnnualExpenseTotal / 12
    const NOINoFinanceMonthly =
      grossRentalIncomeMonthly - totalExpenseMonthly
    const totalReturnFinancingMonthly =
      NOINoFinanceMonthly - mortgagePayment
    const totalReturnNoFinancingMonthly = NOINoFinanceMonthly

    const sqftString = sum(sqft)
      ? sum(sqft).toLocaleString('en')
      : '0'
    return [
      {
        text: ['Financial ', { text: 'Returns', color: '#3399ff' }],
        fontSize: 18,
        margin: [0, 5, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
          // FINANCING DROP SHADOW
          {
            type: 'rect',
            x: 2,
            y: 27,
            w: 175,
            h: 55,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          // FINANCING BLUE CANVAS
          {
            type: 'rect',
            x: 0,
            y: 25,
            w: 175,
            h: 55,
            r: 2,
            color: '#3399ff',
            fillOpacity: 1,
          },
          // CASH DROP SHADOW
          {
            type: 'rect',
            x: 192,
            y: 27,
            w: 175,
            h: 55,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          // CASH DARK BLUE CANVAS
          {
            type: 'rect',
            x: 190,
            y: 25,
            w: 175,
            h: 55,
            r: 2,
            color: '#344265',
            fillOpacity: 1,
          },
          // GROSS YIELD DROP SHADOW
          {
            type: 'rect',
            x: 7,
            y: 92,
            w: 85,
            h: 50,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          // GROSS YIELD WHITE CANVAS
          {
            type: 'rect',
            x: 5,
            y: 90,
            w: 85,
            h: 50,
            r: 2,
          },
          // GROSS YIELD CANVAS BORDER
          {
            type: 'rect',
            x: 5,
            y: 90,
            w: 85,
            h: 50,
            r: 2,
            border: [true, true, true, true],
            color: 'white',
            fillOpacity: 1,
          },
          // CAP RATE DROP SHADOW
          {
            type: 'rect',
            x: 97,
            y: 92,
            w: 85,
            h: 50,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          // CAP RATE WHITE CANVAS
          {
            type: 'rect',
            x: 95,
            y: 90,
            w: 85,
            h: 50,
            r: 2,
          },
          // CAP RATE CANVAS BORDER
          {
            type: 'rect',
            x: 95,
            y: 90,
            w: 85,
            h: 50,
            r: 2,
            border: [true, true, true, true],
            color: 'white',
            fillOpacity: 1,
          },
          // LEVERAGE RATE DROP SHADOW
          {
            type: 'rect',
            x: 187,
            y: 92,
            w: 85,
            h: 50,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          // LEVERAGE RATE WHITE CANVAS
          {
            type: 'rect',
            x: 185,
            y: 90,
            w: 85,
            h: 50,
            r: 2,
          },
          // LEVERAGE RATE CANVAS BORDER
          {
            type: 'rect',
            x: 185,
            y: 90,
            w: 85,
            h: 50,
            r: 2,
            border: [true, true, true, true],
            color: 'white',
            fillOpacity: 1,
          },
          // EQUITY DROP SHADOW
          {
            type: 'rect',
            x: 277,
            y: 92,
            w: 85,
            h: 50,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          // EQUITY WHITE CANVAS
          {
            type: 'rect',
            x: 275,
            y: 90,
            w: 85,
            h: 50,
            r: 2,
          },
          // EQUITY CANVAS BORDER
          {
            type: 'rect',
            x: 275,
            y: 90,
            w: 85,
            h: 50,
            r: 2,
            border: [true, true, true, true],
            color: 'white',
            fillOpacity: 1,
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 360 },
        stack: [
          { text: 'Financing', fontSize: 15, color: 'white' },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 0,
                lineWidth: 1.5,
                color: 'white',
              },
            ],
          },
          {
            text: [
              {
                text: 'Cash Flow/mo: ',
                fontSize: 12,
              },
              {
                text: currencyFormat.format(
                  totalReturnFinancingMonthly,
                ),
                fontSize: 14,
                bold: true,
              },
            ],
            color: 'white',
            margin: [0, 10, 0, 0],
          },
        ],
      },
      {
        absolutePosition: { x: 410, y: 360 },
        stack: [
          { text: 'Cash', fontSize: 15, color: 'white' },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 0,
                lineWidth: 1.5,
                color: 'white',
              },
            ],
          },
          {
            text: [
              {
                text: 'Cash Flow/mo: ',
                fontSize: 12,
              },
              {
                text: currencyFormat.format(
                  totalReturnNoFinancingMonthly,
                ),
                fontSize: 14,
                bold: true,
              },
            ],
            color: 'white',
            margin: [0, 10, 0, 0],
          },
        ],
      },
      IRR !== 0
        ? {
            absolutePosition: { x: -70, y: 430 },
            stack: [
              {
                text: IRR + '%',
                fontSize: 14,
                alignment: 'center',
                bold: true,
              },
              {
                text: 'IRR',
                fontSize: 13,
                alignment: 'center',
              },
            ],
          }
        : {
            absolutePosition: { x: -70, y: 430 },
            stack: [
              {
                text: IRR + '%',
                fontSize: 14,
                alignment: 'center',
                bold: true,
              },
              {
                text: 'IRR',
                fontSize: 13,
                alignment: 'center',
              },
            ],
          },
      CAPRate !== 0
        ? {
            absolutePosition: { x: 115, y: 430 },
            stack: [
              {
                text: CAPRate + '%',
                fontSize: 14,
                bold: true,
                alignment: 'center',
              },
              {
                text: 'CAP Rate',
                fontSize: 13,
                alignment: 'center',
              },
            ],
          }
        : {
            absolutePosition: { x: 115, y: 430 },
            stack: [
              {
                text: '-',
                fontSize: 14,
                bold: true,
                alignment: 'center',
              },
              {
                text: 'CAP Rate',
                fontSize: 13,
                alignment: 'center',
              },
            ],
          },
      cashOnCash !== 0
        ? {
            absolutePosition: { x: 290, y: 430 },
            stack: [
              {
                text: cashOnCash + '%',
                fontSize: 14,
                bold: true,
                alignment: 'center',
              },
              {
                text: 'Cash On Cash',
                fontSize: 13,
                alignment: 'center',
              },
            ],
          }
        : {
            absolutePosition: { x: 290, y: 430 },
            stack: [
              {
                text: '-',
                fontSize: 14,
                bold: true,
                alignment: 'center',
              },
              {
                text: 'Cash On Cash',
                fontSize: 13,
                alignment: 'center',
              },
            ],
          },
      equity >= 0
        ? {
            absolutePosition: { x: 465, y: 430 },
            stack: [
              {
                text: currencyFormat.format(equity),
                fontSize: 14,
                bold: true,
                alignment: 'center',
              },
              {
                text: 'Equity',
                fontSize: 13,
                alignment: 'center',
              },
            ],
          }
        : {
            absolutePosition: { x: 465, y: 430 },
            stack: [
              {
                text: '-',
                fontSize: 14,
                bold: true,
                alignment: 'center',
              },
              {
                text: 'Equity',
                fontSize: 13,
                alignment: 'center',
              },
            ],
          },
      {
        text: [
          'Property ',
          { text: 'Information', color: '#3399ff' },
        ],
        fontSize: 18,
        margin: [0, 30, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
        ],
      },
      {
        columns: [
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              {
                text: [
                  'Beds:   ',
                  { text: sum(beds), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Baths:   ',
                  { text: sum(baths), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Sq Feet:   ',
                  {
                    text: sqftString,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Year:   ',
                  {
                    text: property.year,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
            ],
            margin: [0, 10, 0, 0],
            width: 140,
          },
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              {
                text: [
                  'Monthly Rent:   ',
                  {
                    text: currencyFormat.format(sum(rentAmount)),
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },

              {
                text: [
                  'Property Taxes:   ',
                  {
                    text: currencyFormat.format(propertyTaxes),
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Annual HOA:   ',
                  {
                    text: currencyFormat.format(yearlyHOA),
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
            ],
            margin: [0, 10, 0, 0],
            width: 'auto',
          },
        ],
      },
      buildProformaDescriptionPdfNode(property),
    ]
  }

  /**
   * Generate page 2 top section
   */
  async generatePage2Top() {
    const { property } = this
    const { acquisition = {}, assumptions = {} } = property

    const cashFlow = acquisition.analytics.turnkey.cashFlowTurnkey
      ? acquisition.analytics.turnkey.cashFlowTurnkey
      : 0
    const mortgagePayment = acquisition.analytics.turnkey
      .mortgageTurnkey
      ? acquisition.analytics.turnkey.mortgageTurnkey
      : 0
    const financeAmount = acquisition.analytics.turnkey
      .loanAmountTurnkey
      ? acquisition.analytics.turnkey.loanAmountTurnkey
      : 0
    const netOperatingIncome = acquisition.analytics.turnkey
      .netOperatingIncomeTurnkey
      ? acquisition.analytics.turnkey.netOperatingIncomeTurnkey
      : 0
    const downPaymentTotal = acquisition.analytics.turnkey
      .downPaymentTotalTurnkey
      ? acquisition.analytics.turnkey.downPaymentTotalTurnkey
      : 0
    const pointsAmount = acquisition.analytics.turnkey
      .pointsAmountTurnkey
      ? acquisition.analytics.turnkey.pointsAmountTurnkey
      : 0
    const closeCostDollar = acquisition.analytics.turnkey
      .closeCostDollarTurnkey
      ? acquisition.analytics.turnkey.closeCostDollarTurnkey
      : 0
    const closeCostPercent = acquisition.analytics.turnkey
      .closeCostPercentTurnkey
      ? acquisition.analytics.turnkey.closeCostPercentTurnkey
      : 0
    const paydownMonthly = acquisition.analytics.turnkey
      .paydownMonthlyTurnkey
      ? acquisition.analytics.turnkey.paydownMonthlyTurnkey
      : 0
    const acquisitionCommissionDollar = acquisition.analytics
      .acquisitionCommissionDollarTurnkey
      ? acquisition.analytics.acquisitionCommissionDollarTurnkey
      : 0
    const acquisitionCommissionPercent = acquisition.analytics
      .acquisitionCommissionPercentTurnkey
      ? acquisition.analytics.acquisitionCommissionPercentTurnkey
      : 0
    const propertyManagementDollar = acquisition.analytics.turnkey
      .propertyManagementDollarTurnkey
      ? acquisition.analytics.turnkey.propertyManagementDollarTurnkey
      : 0
    const utilities = acquisition.analytics.turnkey
      .utilitiesTypeDollarTurnkey
      ? acquisition.analytics.turnkey.utilitiesTypeDollarTurnkey
      : 0
    const utilitiesPercent = assumptions.utilitiesTurnkey
      ? assumptions.utilitiesTurnkey
      : 0
    const financingFeeTotal = acquisition.analytics.turnkey
      .financingFeeTotalTurnkey
      ? acquisition.analytics.turnkey.financingFeeTotalTurnkey
      : 0
    const assetManagementFeeTotal = acquisition.analytics.turnkey
      .assetManagementFeeTotalTurnkey
      ? acquisition.analytics.turnkey.assetManagementFeeTotalTurnkey
      : 0
    const homeInsurance = acquisition.analytics.turnkey
      .propertyInsuranceDollarTurnkey
      ? acquisition.analytics.turnkey.propertyInsuranceDollarTurnkey
      : 0
    const maintenanceDollar = acquisition.analytics.turnkey
      .maintenanceTurnkeyDollar
      ? acquisition.analytics.turnkey.maintenanceTurnkeyDollar
      : 0

    const rentAmount =
      assumptions.rentType === 'rentRange'
        ? acquisition.analysis.rentLow &&
          acquisition.analysis.rentHigh
          ? map(
              zip(
                acquisition.analysis.rentLow,
                acquisition.analysis.rentHigh,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rent
          ? acquisition.analysis.rent
          : [0]
    const otherIncome = acquisition.analysis.otherIncome
      ? acquisition.analysis.otherIncome
      : [0]
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.turnkey.hoaTotal
      ? acquisition.analytics.turnkey.hoaTotal / 12
      : 0
    const afterRepairValue = acquisition.analytics.turnkey
      .arvTotalTurnkey
      ? acquisition.analytics.turnkey.arvTotalTurnkey
      : 0
    const offerPrice = acquisition.analysis.resellPriceTurnkey
      ? acquisition.analysis.resellPriceTurnkey
      : afterRepairValue
    const financingRepairsPercentage = acquisition.analysis
      .financingRepairsPercentageTurnkey
      ? acquisition.analysis.financingRepairsPercentageTurnkey
      : 0
    const rehabCost = acquisition.analytics.turnkey.corTotalTurnkey
      ? acquisition.analytics.turnkey.corTotalTurnkey
      : 0

    const downPayment = assumptions.downPaymentTurnkey
      ? assumptions.downPaymentTurnkey
      : 0
    const rate = assumptions.rateTurnkey ? assumptions.rateTurnkey : 0
    const term = assumptions.termTurnkey ? assumptions.termTurnkey : 0
    const termType = assumptions.termTurnkeyType
      ? assumptions.termTurnkeyType
      : 'Months'
    const vacancy = assumptions.vacancy ? assumptions.vacancy : 0
    const maintenance = assumptions.maintenanceTurnkey
      ? assumptions.maintenanceTurnkey
      : 0
    const propertyMgmtPercent = acquisition.analytics.turnkey
      .propertyManagementPercentageTurnkey
      ? acquisition.analytics.turnkey
          .propertyManagementPercentageTurnkey
      : 0
    const pointPercentage = assumptions.pointsTurnkey
      ? assumptions.pointsTurnkey
      : 0
    const homePriceAppreciation = assumptions.homePriceAppreciation
      ? assumptions.homePriceAppreciation
      : 0
    const payDownAccelerator = assumptions.payDownAccelerator
      ? assumptions.payDownAccelerator
      : 0
    const annualRevenueIncrease = assumptions.growthRateIncome
      ? assumptions.growthRateIncome
      : 0
    const annualOperatingExpenseIncrease =
      assumptions.growthRateExpense
        ? assumptions.growthRateExpense
        : 0
    const acquisitionFee = assumptions.acquisitionFee
      ? assumptions.acquisitionFee
      : 0
    const creditFromSeller = assumptions.creditFromSeller
      ? assumptions.creditFromSeller
      : 0
    const financingFee = acquisition.analytics.turnkey
      .financingFeePercentTurnkey
      ? acquisition.analytics.turnkey.financingFeePercentTurnkey
      : 0
    const otherExpenses = assumptions.otherExpenseTurnkey
      ? assumptions.otherExpenseTurnkey
      : 0
    const ownerReserves = assumptions.ownerReservesTurnkey
      ? assumptions.ownerReservesTurnkey
      : 0

    const customAcquisitionExpenseTotal = acquisition.analytics
      .customAcquisitionExpenseTotal
      ? acquisition.analytics.customAcquisitionExpenseTotal
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const monthlyRate = rate / 12 / 100
    const financedRenovationAmount =
      rehabCost - rehabCost * (downPayment / 100) || 0
    const renovationAmount = rehabCost || 0

    const graphTest = graphSection({
      financeAmount,
      mortgagePayment,
      monthlyRate,
      debtYear: termType === 'Years' ? term * 12 : term,
      paydownMonthly,
      holdingPeriod: false,
    })
    const appreciation = appreciationSection({
      afterRepairValue,
      homePriceAppreciation,
      term: termType === 'Years' ? term * 12 : term,
    })

    const totalInitialInvestmentFinancing =
      downPaymentTotal +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      financingFeeTotal +
      pointsAmount -
      creditFromSeller +
      renovationAmount * (1 - financingRepairsPercentage / 100) +
      customAcquisitionExpenseTotal
    const totalInitialInvestmentNoFinancing =
      offerPrice +
      renovationAmount +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      pointsAmount +
      financingFeeTotal +
      ownerReserves +
      customAcquisitionExpenseTotal
    const cashFlowNoFinancing = netOperatingIncome
    const yearlyHOA = monthlyHOA * 12
    const rentalIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const vacancyRate1 =
      sum(
        map(
          zip(rentAmount, vacancy),
          ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
        ),
      ) * 12
    const vacancyRate3 = sum(
      map(
        zip(rentalIncome3, vacancy),
        ([rentalIncome3, vacancy]) => rentalIncome3 * (vacancy / 100),
      ),
    )
    const vacancyRate5 = sum(
      map(
        zip(rentalIncome5, vacancy),
        ([rentalIncome5, vacancy]) => rentalIncome5 * (vacancy / 100),
      ),
    )
    const vacancyRate10 = sum(
      map(
        zip(rentalIncome10, vacancy),
        ([rentalIncome10, vacancy]) =>
          rentalIncome10 * (vacancy / 100),
      ),
    )
    const grossRentalIncome1 = sum(rentAmount) * 12 - vacancyRate1
    const grossRentalIncome3 = sum(rentalIncome3) - vacancyRate3
    const grossRentalIncome5 = sum(rentalIncome5) - vacancyRate5
    const grossRentalIncome10 = sum(rentalIncome10) - vacancyRate10
    const propertyTaxes3 = expensesSection({
      expenseType: 'PT',
      revenueYear: 3,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes5 = expensesSection({
      expenseType: 'PT',
      revenueYear: 5,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes10 = expensesSection({
      expenseType: 'PT',
      revenueYear: 10,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance3 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 3,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance5 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 5,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance10 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 10,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA3 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 3,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA5 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 5,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA10 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 10,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyManagement1 = propertyManagementDollar
    const propertyManagement3 =
      assumptions.propertyManagementTurnkeyType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome3) * (propertyMgmtPercent / 100)
    const propertyManagement5 =
      assumptions.propertyManagementTurnkeyType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome5) * (propertyMgmtPercent / 100)
    const propertyManagement10 =
      assumptions.propertyManagementTurnkeyType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome10) * (propertyMgmtPercent / 100)
    const maintenance1 = maintenanceDollar
    const maintenance3 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance5 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance10 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const utilities1 = utilities * 12
    const utilities3 =
      assumptions.utilitiesTurnkeyType === '$'
        ? utilities * 12
        : sum(rentalIncome3) * (utilitiesPercent / 100)
    const utilities5 =
      assumptions.utilitiesTurnkeyType === '$'
        ? utilities * 12
        : sum(rentalIncome5) * (utilitiesPercent / 100)
    const utilities10 =
      assumptions.utilitiesTurnkeyType === '$'
        ? utilities * 12
        : sum(rentalIncome10) * (utilitiesPercent / 100)
    const assetFee1 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 1,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee3 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee5 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee10 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const otherExpense3 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense5 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense10 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal3 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal5 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal10 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const totalExpense1 =
      propertyTaxes +
      homeInsurance +
      yearlyHOA +
      propertyManagement1 +
      maintenance1 +
      utilities1 +
      assetFee1 +
      otherExpenses +
      customAnnualExpenseTotal
    const totalExpense3 =
      propertyTaxes3 +
      homeInsurance3 +
      HOA3 +
      propertyManagement3 +
      maintenance3 +
      utilities3 +
      assetFee3 +
      otherExpense3 +
      customAnnualExpenseTotal3
    const totalExpense5 =
      propertyTaxes5 +
      homeInsurance5 +
      HOA5 +
      propertyManagement5 +
      maintenance5 +
      utilities5 +
      assetFee5 +
      otherExpense5 +
      customAnnualExpenseTotal5
    const totalExpense10 =
      propertyTaxes10 +
      homeInsurance10 +
      HOA10 +
      propertyManagement10 +
      maintenance10 +
      utilities10 +
      assetFee10 +
      otherExpense10 +
      customAnnualExpenseTotal10
    const NOINoFinance1 = grossRentalIncome1 - totalExpense1
    const NOINoFinance3 = grossRentalIncome3 - totalExpense3
    const NOINoFinance5 = grossRentalIncome5 - totalExpense5
    const NOINoFinance10 = grossRentalIncome10 - totalExpense10
    const mortgagePaymentYearly = mortgagePayment * 12
    const totalReturnFinancing = []
    const totalReturnFinancing1 =
      NOINoFinance1 - mortgagePaymentYearly
    const totalReturnFinancing3 =
      NOINoFinance3 - mortgagePaymentYearly
    const totalReturnFinancing5 =
      NOINoFinance5 - mortgagePaymentYearly
    const totalReturnFinancing10 =
      NOINoFinance10 - mortgagePaymentYearly
    totalReturnFinancing.push({
      one: totalReturnFinancing1,
      three: totalReturnFinancing3,
      five: totalReturnFinancing5,
      ten: totalReturnFinancing10,
    })
    //const totalROIFinancingMonthly = ((NOINoFinanceMonthly -  mortgagePayment)/(downPaymentTotal + ((closeCostPercent/100) * financeAmount))) * 100
    const totalROIFinancing1 =
      (cashFlow / totalInitialInvestmentFinancing) * 100
    const totalReturnNoFinancing1 = NOINoFinance1
    const totalROINoFinancing1 =
      (totalReturnNoFinancing1 / totalInitialInvestmentNoFinancing) *
      100

    const cashFlowAccumulation = cashFlowAccumulationCalc({
      accelerator: payDownAccelerator,
      term: termType === 'Years' ? term * 12 + 1 : term + 1,
      rentAmount: rentAmount || [0],
      vacancy: vacancy
        ? map(vacancy, (vacancy) => vacancy / 100)
        : [0],
      propertyTaxes: propertyTaxes / 12,
      homeInsurance: homeInsurance / 12,
      monthlyHOA,
      propertyMgmtPercent: propertyMgmtPercent / 100,
      maintenance: maintenance / 100,
      utilities: utilities,
      annualRevenueIncrease: annualRevenueIncrease / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
      mortgagePayment,
    })
    const graphDataURL = getChartData(
      property,
      graphTest,
      totalReturnFinancing,
      appreciation,
      cashFlowAccumulation,
      totalInitialInvestmentFinancing,
    )

    return [
          {
            width: 400,
            stack: [
              {
                text: [
                  'Financial ',
                  { text: 'Projections', color: '#3399ff' },
                ],
                fontSize: 18,
                margin: [15, 20, 0, 0],
              },
              {
                canvas: [
                  {
                    type: 'line',
                    x1: 15,
                    y1: 5,
                    x2: 150,
                    y2: 5,
                    lineWidth: 2,
                  },
                ],
              },
              {
                image: graphDataURL,
                width: 400,
                height: 275,
                margin: [10, 20, 0, 0],
              },
            ],
          },
          [
            {
              margin: [20, 30],
              stack: [
                {
                  table: {
                    headerRows: 1,
                    widths: ['*', '*'],
                    body: [
                      [
                        {
                          text: 'Financing Assumptions',
                          fillColor: '#3399ff',
                          color: 'white',
                          fontSize: 8,
                          colSpan: 2,
                          alignment: 'center',
                        },
                        { text: '' },
                      ],
                      [
                        {
                          text: 'Loan Amount',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(financeAmount),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text: 'Financed Rehab Amount',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            (financedRenovationAmount *
                              financingRepairsPercentage) /
                              100,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text: 'Interest Rate',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: rate + '%',
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text: 'Mortgage (yrs)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: (termType === 'Years'
                            ? term
                            : term / 12
                          ).toFixed(1),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text: 'Mortgage Payment',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            mortgagePayment,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text:
                            'Paydown Acc. (' +
                            payDownAccelerator +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(paydownMonthly),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                    ],
                  },
                  layout: {
                    defaultBorder: false,
                  },
                },
                {
                  table: {
                    headerRows: 1,
                    widths: ['*', '*'],
                    body: [
                      [
                        {
                          text: 'Intial Investment',
                          fillColor: '#3399ff',
                          color: 'white',
                          fontSize: 8,
                          colSpan: 2,
                          alignment: 'center',
                        },
                        { text: '' },
                      ],
                      [
                        {
                          text: 'Down Payment (' + downPayment + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            downPaymentTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      ...(creditFromSeller
                        ? [
                            [
                              {
                                text: '-Credit From Seller',
                                alignment: 'left',
                                fontSize: 6,
                              },
                              {
                                text: currencyFormat.format(
                                  -1 * creditFromSeller,
                                ),
                                alignment: 'right',
                                fontSize: 6,
                              },
                            ],
                          ]
                        : []),
                      [
                        {
                          text: 'Rehab Amount',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            renovationAmount *
                              (1 - financingRepairsPercentage / 100),
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text: 'Acquisition Fee',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(acquisitionFee),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text:
                            'Acquisition Commission (' +
                            acquisitionCommissionPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            acquisitionCommissionDollar,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text:
                            'Est Closing Cost (' +
                            closeCostPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            closeCostDollar,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text:
                            'Financing Fee (' + financingFee + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            financingFeeTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text: 'Custom Expenses',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            customAcquisitionExpenseTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text: 'Points (' + pointPercentage + '%)',
                          alignment: 'left',
                          fontSize: 6,
                          border: [false, false, false, true],
                        },
                        {
                          text: currencyFormat.format(pointsAmount),
                          alignment: 'right',
                          fontSize: 6,
                          border: [false, false, false, true],
                        },
                      ],
                      [
                        {
                          text: 'Total Out Of Pocket',
                          alignment: 'left',
                          fontSize: 6,
                          bold: true,
                        },
                        {
                          text: currencyFormat.format(
                            totalInitialInvestmentFinancing,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                          bold: true,
                        },
                      ],
                    ],
                  },
                  layout: {
                    defaultBorder: false,
                  },
                  margin: [0, 10, 0, 0],
                },
                {
                  table: {
                    headerRows: 1,
                    widths: ['*', '*'],
                    body: [
                      [
                        {
                          text: 'Property KPI Without Financing',
                          fillColor: '#3399ff',
                          color: 'white',
                          fontSize: 8,
                          colSpan: 2,
                          alignment: 'center',
                        },
                        { text: '' },
                      ],
                      [
                        {
                          text: 'Annual Cash Flow',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            cashFlowNoFinancing,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text: 'All In Cost',
                          alignment: 'left',
                          fontSize: 6,
                          border: [false, false, false, true],
                        },
                        {
                          text: currencyFormat.format(
                            totalInitialInvestmentNoFinancing,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                          border: [false, false, false, true],
                        },
                      ],
                      [
                        {
                          text: 'Cap Rate',
                          alignment: 'left',
                          fontSize: 6,
                          bold: true,
                        },
                        {
                          text: totalROINoFinancing1.toFixed(2) + '%',
                          alignment: 'right',
                          fontSize: 6,
                          bold: true,
                        },
                      ],
                    ],
                  },
                  layout: {
                    defaultBorder: false,
                  },
                  margin: [0, 10, 0, 0],
                },
                {
                  table: {
                    headerRows: 1,
                    widths: ['*', '*'],
                    body: [
                      [
                        {
                          text: 'Property KPI With Financing',
                          fillColor: '#3399ff',
                          color: 'white',
                          fontSize: 8,
                          colSpan: 2,
                          alignment: 'center',
                        },
                        { text: '' },
                      ],
                      [
                        {
                          text: 'Annual Cash Flow',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(cashFlow),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ],
                      [
                        {
                          text: 'Total Out Of Pocket',
                          alignment: 'left',
                          fontSize: 6,
                          border: [false, false, false, true],
                        },
                        {
                          text: currencyFormat.format(
                            totalInitialInvestmentFinancing,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                          border: [false, false, false, true],
                        },
                      ],
                      [
                        {
                          text: 'Cash On Cash',
                          alignment: 'left',
                          fontSize: 6,
                          bold: true,
                        },
                        {
                          text: totalROIFinancing1.toFixed(2) + '%',
                          alignment: 'right',
                          fontSize: 6,
                          bold: true,
                        },
                      ],
                    ],
                  },
                  layout: {
                    defaultBorder: false,
                  },
                  margin: [0, 10, 0, 0],
                },
              ],
            },
          ],
    ]
  }

  /**
   * Generate page 2 bottom section
   */
  async generatePage2Bottom() {
    const { property } = this

    const {
      acquisition = {},
      assumptions = {},
      portfolio = {},
    } = property
    const { organization = {} } = portfolio
    const { legalDescription = '' } = organization
    const disclaimerNode = buildProformaDisclaimerPdfNode(legalDescription)

    const mortgagePayment = acquisition.analytics.turnkey
      .mortgageTurnkey
      ? acquisition.analytics.turnkey.mortgageTurnkey
      : 0
    const downPaymentTotal = acquisition.analytics.turnkey
      .downPaymentTotalTurnkey
      ? acquisition.analytics.turnkey.downPaymentTotalTurnkey
      : 0
    const pointsAmount = acquisition.analytics.turnkey
      .pointsAmountTurnkey
      ? acquisition.analytics.turnkey.pointsAmountTurnkey
      : 0
    const closeCostDollar = acquisition.analytics.turnkey
      .closeCostDollarTurnkey
      ? acquisition.analytics.turnkey.closeCostDollarTurnkey
      : 0
    const acquisitionCommissionDollar = acquisition.analytics
      .acquisitionCommissionDollarTurnkey
      ? acquisition.analytics.acquisitionCommissionDollarTurnkey
      : 0
    const propertyManagementDollar = acquisition.analytics.turnkey
      .propertyManagementDollarTurnkey
      ? acquisition.analytics.turnkey.propertyManagementDollarTurnkey
      : 0
    const utilities = acquisition.analytics.turnkey
      .utilitiesTypeDollarTurnkey
      ? acquisition.analytics.turnkey.utilitiesTypeDollarTurnkey
      : 0
    const utilitiesPercent = assumptions.utilitiesTurnkey
      ? assumptions.utilitiesTurnkey
      : 0
    const financingFeeTotal = acquisition.analytics.turnkey
      .financingFeeTotalTurnkey
      ? acquisition.analytics.turnkey.financingFeeTotalTurnkey
      : 0
    const assetManagementFeeTotal = acquisition.analytics.turnkey
      .assetManagementFeeTotalTurnkey
      ? acquisition.analytics.turnkey.assetManagementFeeTotalTurnkey
      : 0
    const homeInsurance = acquisition.analytics.turnkey
      .propertyInsuranceDollarTurnkey
      ? acquisition.analytics.turnkey.propertyInsuranceDollarTurnkey
      : 0
    const maintenanceDollar = acquisition.analytics.turnkey
      .maintenanceTurnkeyDollar
      ? acquisition.analytics.turnkey.maintenanceTurnkeyDollar
      : 0

    const rentAmount =
      assumptions.rentType === 'rentRange'
        ? acquisition.analysis.rentLow &&
          acquisition.analysis.rentHigh
          ? map(
              zip(
                acquisition.analysis.rentLow,
                acquisition.analysis.rentHigh,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rent
          ? acquisition.analysis.rent
          : [0]
    const otherIncome = acquisition.analysis.otherIncome
      ? acquisition.analysis.otherIncome
      : [0]
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.turnkey.hoaTotal
      ? acquisition.analytics.turnkey.hoaTotal / 12
      : 0
    const afterRepairValue = acquisition.analytics.turnkey
      .arvTotalTurnkey
      ? acquisition.analytics.turnkey.arvTotalTurnkey
      : 0
    const offerPrice = acquisition.analysis.resellPriceTurnkey
      ? acquisition.analysis.resellPriceTurnkey
      : afterRepairValue
    const financingRepairsPercentage = acquisition.analysis
      .financingRepairsPercentageTurnkey
      ? acquisition.analysis.financingRepairsPercentageTurnkey
      : 0
    const rehabCost = acquisition.analytics.turnkey.corTotalTurnkey
      ? acquisition.analytics.turnkey.corTotalTurnkey
      : 0

    const vacancy = assumptions.vacancy ? assumptions.vacancy : 0
    const maintenance = assumptions.maintenanceTurnkey
      ? assumptions.maintenanceTurnkey
      : 0
    const propertyMgmtPercent = acquisition.analytics.turnkey
      .propertyManagementPercentageTurnkey
      ? acquisition.analytics.turnkey
          .propertyManagementPercentageTurnkey
      : 0
    const annualRevenueIncrease = assumptions.growthRateIncome
      ? assumptions.growthRateIncome
      : 0
    const annualOperatingExpenseIncrease =
      assumptions.growthRateExpense
        ? assumptions.growthRateExpense
        : 0
    const acquisitionFee = assumptions.acquisitionFee
      ? assumptions.acquisitionFee
      : 0
    const creditFromSeller = assumptions.creditFromSeller
      ? assumptions.creditFromSeller
      : 0
    const otherExpenses = assumptions.otherExpenseTurnkey
      ? assumptions.otherExpenseTurnkey
      : 0
    const ownerReserves = assumptions.ownerReservesTurnkey
      ? assumptions.ownerReservesTurnkey
      : 0

    const customAcquisitionExpenseTotal = acquisition.analytics
      .customAcquisitionExpenseTotal
      ? acquisition.analytics.customAcquisitionExpenseTotal
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const renovationAmount = rehabCost || 0

    const totalInitialInvestmentFinancing =
      downPaymentTotal +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      financingFeeTotal +
      pointsAmount -
      creditFromSeller +
      renovationAmount * (1 - financingRepairsPercentage / 100) +
      customAcquisitionExpenseTotal
    const totalInitialInvestmentNoFinancing =
      offerPrice +
      renovationAmount +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      pointsAmount +
      financingFeeTotal +
      ownerReserves +
      customAcquisitionExpenseTotal
    const yearlyHOA = monthlyHOA * 12
    const rentalIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const otherIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const otherIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const otherIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const vacancyRateMonthly = sum(
      map(
        zip(rentAmount, vacancy),
        ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
      ),
    )
    const vacancyRate1 =
      sum(
        map(
          zip(rentAmount, vacancy),
          ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
        ),
      ) * 12
    const vacancyRate3 = sum(
      map(
        zip(rentalIncome3, vacancy),
        ([rentalIncome3, vacancy]) => rentalIncome3 * (vacancy / 100),
      ),
    )
    const vacancyRate5 = sum(
      map(
        zip(rentalIncome5, vacancy),
        ([rentalIncome5, vacancy]) => rentalIncome5 * (vacancy / 100),
      ),
    )
    const vacancyRate10 = sum(
      map(
        zip(rentalIncome10, vacancy),
        ([rentalIncome10, vacancy]) =>
          rentalIncome10 * (vacancy / 100),
      ),
    )
    const grossRentalIncomeMonthly =
      sum(rentAmount) - vacancyRateMonthly
    const grossRentalIncome1 = sum(rentAmount) * 12 - vacancyRate1
    const grossRentalIncome3 = sum(rentalIncome3) - vacancyRate3
    const grossRentalIncome5 = sum(rentalIncome5) - vacancyRate5
    const grossRentalIncome10 = sum(rentalIncome10) - vacancyRate10
    const monthlyRevenue = grossRentalIncomeMonthly + sum(otherIncome)
    const revenueTotal1 = grossRentalIncome1 + sum(otherIncome) * 12
    const revenueTotal3 = grossRentalIncome3 + sum(otherIncome3)
    const revenueTotal5 = grossRentalIncome5 + sum(otherIncome5)
    const revenueTotal10 = grossRentalIncome10 + sum(otherIncome10)
    const propertyTaxes3 = expensesSection({
      expenseType: 'PT',
      revenueYear: 3,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes5 = expensesSection({
      expenseType: 'PT',
      revenueYear: 5,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes10 = expensesSection({
      expenseType: 'PT',
      revenueYear: 10,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance3 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 3,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance5 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 5,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance10 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 10,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA3 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 3,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA5 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 5,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA10 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 10,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyManagement1 = propertyManagementDollar
    const propertyManagement3 =
      assumptions.propertyManagementTurnkeyType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome3) * (propertyMgmtPercent / 100)
    const propertyManagement5 =
      assumptions.propertyManagementTurnkeyType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome5) * (propertyMgmtPercent / 100)
    const propertyManagement10 =
      assumptions.propertyManagementTurnkeyType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome10) * (propertyMgmtPercent / 100)
    const maintenance1 = maintenanceDollar
    const maintenance3 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance5 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance10 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const utilities1 = utilities * 12
    const utilities3 =
      assumptions.utilitiesTurnkeyType === '$'
        ? utilities * 12
        : sum(rentalIncome3) * (utilitiesPercent / 100)
    const utilities5 =
      assumptions.utilitiesTurnkeyType === '$'
        ? utilities * 12
        : sum(rentalIncome5) * (utilitiesPercent / 100)
    const utilities10 =
      assumptions.utilitiesTurnkeyType === '$'
        ? utilities * 12
        : sum(rentalIncome10) * (utilitiesPercent / 100)
    const assetFee1 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 1,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee3 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee5 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee10 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const otherExpense3 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense5 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense10 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal3 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal5 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal10 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const totalExpenseMonthly =
      propertyTaxes / 12 +
      homeInsurance / 12 +
      monthlyHOA +
      (propertyMgmtPercent / 100) * grossRentalIncomeMonthly +
      (maintenance / 100) * grossRentalIncomeMonthly +
      utilities +
      assetManagementFeeTotal / 12 +
      otherExpenses / 12 +
      customAnnualExpenseTotal / 12
    const totalExpense1 =
      propertyTaxes +
      homeInsurance +
      yearlyHOA +
      propertyManagement1 +
      maintenance1 +
      utilities1 +
      assetFee1 +
      otherExpenses +
      customAnnualExpenseTotal
    const totalExpense3 =
      propertyTaxes3 +
      homeInsurance3 +
      HOA3 +
      propertyManagement3 +
      maintenance3 +
      utilities3 +
      assetFee3 +
      otherExpense3 +
      customAnnualExpenseTotal3
    const totalExpense5 =
      propertyTaxes5 +
      homeInsurance5 +
      HOA5 +
      propertyManagement5 +
      maintenance5 +
      utilities5 +
      assetFee5 +
      otherExpense5 +
      customAnnualExpenseTotal5
    const totalExpense10 =
      propertyTaxes10 +
      homeInsurance10 +
      HOA10 +
      propertyManagement10 +
      maintenance10 +
      utilities10 +
      assetFee10 +
      otherExpense10 +
      customAnnualExpenseTotal10
    const expenseRatio1 = (totalExpense1 / grossRentalIncome1) * 100
    const expenseRatio3 = (totalExpense3 / grossRentalIncome3) * 100
    const expenseRatio5 = (totalExpense5 / grossRentalIncome5) * 100
    const expenseRatio10 =
      (totalExpense10 / grossRentalIncome10) * 100
    const NOINoFinanceMonthly =
      grossRentalIncomeMonthly - totalExpenseMonthly
    const NOINoFinance1 = grossRentalIncome1 - totalExpense1
    const NOINoFinance3 = grossRentalIncome3 - totalExpense3
    const NOINoFinance5 = grossRentalIncome5 - totalExpense5
    const NOINoFinance10 = grossRentalIncome10 - totalExpense10
    const NOIFinanceMonthly =
      grossRentalIncomeMonthly - totalExpenseMonthly
    const NOIFinance1 = grossRentalIncome1 - totalExpense1
    const NOIFinance3 = grossRentalIncome3 - totalExpense3
    const NOIFinance5 = grossRentalIncome5 - totalExpense5
    const NOIFinance10 = grossRentalIncome10 - totalExpense10
    const mortgagePaymentYearly = mortgagePayment * 12
    const totalReturnFinancing = []
    const totalReturnFinancing1 =
      NOINoFinance1 - mortgagePaymentYearly
    const totalReturnFinancing3 =
      NOINoFinance3 - mortgagePaymentYearly
    const totalReturnFinancing5 =
      NOINoFinance5 - mortgagePaymentYearly
    const totalReturnFinancing10 =
      NOINoFinance10 - mortgagePaymentYearly
    totalReturnFinancing.push({
      one: totalReturnFinancing1,
      three: totalReturnFinancing3,
      five: totalReturnFinancing5,
      ten: totalReturnFinancing10,
    })
    const totalROIFinancing1 =
      (totalReturnFinancing1 / totalInitialInvestmentFinancing) * 100
    const totalROIFinancing3 =
      (totalReturnFinancing3 / totalInitialInvestmentFinancing) * 100
    const totalROIFinancing5 =
      (totalReturnFinancing5 / totalInitialInvestmentFinancing) * 100
    const totalROIFinancing10 =
      (totalReturnFinancing10 / totalInitialInvestmentFinancing) * 100
    const totalReturnNoFinancing1 = NOINoFinance1
    const totalReturnNoFinancing3 = NOINoFinance3
    const totalReturnNoFinancing5 = NOINoFinance5
    const totalReturnNoFinancing10 = NOINoFinance10
    const totalROINoFinancing1 =
      (totalReturnNoFinancing1 / totalInitialInvestmentNoFinancing) *
      100
    const totalROINoFinancing3 =
      (totalReturnNoFinancing3 / totalInitialInvestmentNoFinancing) *
      100
    const totalROINoFinancing5 =
      (totalReturnNoFinancing5 / totalInitialInvestmentNoFinancing) *
      100
    const totalROINoFinancing10 =
      (totalReturnNoFinancing10 / totalInitialInvestmentNoFinancing) *
      100

    return [
      {
        margin: [15, 10, 15, 0],
        table: {
          headerRows: 0,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Annual Revenue Increase',
                alignment: 'left',
                fontSize: 6,
              },
              { text: '' },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Annual Operating Expense Increase',
                alignment: 'left',
                fontSize: 6,
              },
              { text: '' },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Annual Expense Ratio',
                alignment: 'left',
                fontSize: 6,
              },
              { text: '' },
              {
                text: expenseRatio1.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: expenseRatio3.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: expenseRatio5.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: expenseRatio10.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 0,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              { text: '', alignment: 'left', fontSize: 6 },
              {
                text: 'Monthly',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 1',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 3',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 5',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 10',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 0, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: compact([
            [
              {
                text: 'Revenues',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            sum(rentAmount)
              ? [
                  {
                    text: 'Potential Rental Income',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentAmount)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentAmount) * 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentalIncome3)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentalIncome5)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(rentalIncome10)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            vacancyRate1
              ? [
                  {
                    text:
                      'Vacancy Rate (' +
                      percentageFormat.format(
                        vacancyRateMonthly / sum(rentAmount),
                      ) +
                      ')',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRateMonthly),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRate1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRate3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRate5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(vacancyRate10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            sum(otherIncome)
              ? [
                  {
                    text: 'Other Income',
                    alignment: 'left',
                    fontSize: 6,
                    border: [false, false, false, true],
                  },
                  {
                    text: currencyFormat.format(sum(otherIncome)),
                    alignment: 'right',
                    fontSize: 6,
                    border: [false, false, false, true],
                  },
                  {
                    text: currencyFormat.format(
                      sum(otherIncome) * 12,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                    border: [false, false, false, true],
                  },
                  {
                    text: currencyFormat.format(otherIncome3),
                    alignment: 'right',
                    fontSize: 6,
                    border: [false, false, false, true],
                  },
                  {
                    text: currencyFormat.format(otherIncome5),
                    alignment: 'right',
                    fontSize: 6,
                    border: [false, false, false, true],
                  },
                  {
                    text: currencyFormat.format(otherIncome10),
                    alignment: 'right',
                    fontSize: 6,
                    border: [false, false, false, true],
                  },
                ]
              : null,
            [
              {
                text: 'Gross Rental Income',
                alignment: 'left',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(monthlyRevenue),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(revenueTotal1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(revenueTotal3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(revenueTotal5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(revenueTotal10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
          ]),
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Expenses',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            [
              {
                text: 'Estimated Annual Property Taxes',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(propertyTaxes / 12),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(propertyTaxes),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(propertyTaxes3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(propertyTaxes5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(propertyTaxes10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Estimated Annual Homeowners Insurance',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(homeInsurance / 12),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(homeInsurance),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(homeInsurance3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(homeInsurance5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(homeInsurance10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Estimated Annual HOA Dues',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(monthlyHOA),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(yearlyHOA),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(HOA3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(HOA5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(HOA10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text:
                  'Estimated Annual Prop Mgmt Fee (' +
                  propertyMgmtPercent +
                  '%)',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(
                  (propertyMgmtPercent / 100) *
                    grossRentalIncomeMonthly,
                ),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(propertyManagement1),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(propertyManagement3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(propertyManagement5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(propertyManagement10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Maintenance & Repairs (' + maintenance + '%)',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(
                  (maintenance / 100) * grossRentalIncomeMonthly,
                ),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(maintenance1),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(maintenance3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(maintenance5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(maintenance10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Utilities',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(utilities),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(utilities1),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(utilities3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(utilities5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(utilities10),
                alignment: 'right',
                fontSize: 6,
              },
            ],

            [
              {
                text: 'Other Expenses',
                alignment: 'left',
                fontSize: 6,
                //border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(otherExpenses / 12),
                alignment: 'right',
                fontSize: 6,
                //border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(otherExpenses),
                alignment: 'right',
                fontSize: 6,
                //border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(otherExpense3),
                alignment: 'right',
                fontSize: 6,
                //border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(otherExpense5),
                alignment: 'right',
                fontSize: 6,
                //border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(otherExpense10),
                alignment: 'right',
                fontSize: 6,
                //border: [false, false, false, true],
              },
            ],
            [
              {
                text: 'Custom Expenses',
                alignment: 'left',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(
                  customAnnualExpenseTotal / 12,
                ),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(customAnnualExpenseTotal),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(
                  customAnnualExpenseTotal3,
                ),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(
                  customAnnualExpenseTotal5,
                ),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(
                  customAnnualExpenseTotal10,
                ),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
            ],
            [
              {
                text: 'Total Expenses',
                alignment: 'left',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalExpenseMonthly),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalExpense1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalExpense3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalExpense5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalExpense10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Returns Without Financing',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            [
              {
                text: 'NOI (Cash Available)',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinanceMonthly),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance1),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Net Cashflow',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
            ],
            [
              {
                text: 'Cap Rate',
                alignment: 'left',
                fontSize: 6,
                bold: true,
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: totalROINoFinancing1.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: totalROINoFinancing3.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: totalROINoFinancing5.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: totalROINoFinancing10.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Returns With Financing',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            [
              {
                text: 'NOI (Cash Available)',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinanceMonthly),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance1),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Mortgage',
                alignment: 'left',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePayment),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
            ],
            [
              {
                text: 'Net Cashflow',
                alignment: 'left',
                fontSize: 6,
                bold: true,
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
            [
              {
                text: 'Cash On Cash',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing1.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing3.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing5.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing10.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      ...(disclaimerNode ? [disclaimerNode] : []),
    ]
  }
}
